<template>
  <div class="announcement-list">
    <div class="announcement-list-list__filters app-list-filters">
      <select-field
        class="app-list-filters__field announcement-list__field"
        v-model.trim="filters.status"
        :label="'announcement-list.lbl-status' | globalize"
      >
        <option
          v-for="(item, id) in ANNOUNCEMENT_STATUSES"
          :key="`${item}-${id}`"
          :value="item.id"
        >
          <announcement-status
            :icon-type="item.icon"
            :status="item.label" />
        </option>
      </select-field>

      <select-field
        v-if="networks.length > 0"
        class="app-list-filters__field announcement-list__field"
        v-model.trim="filters.chainId"
        :label="'announcement-list.lbl-network' | globalize"
      >
        <option
          v-for="(item, id) in networks"
          :key="`${item}-${id}`"
          :value="item.id"
        >
          {{ item.name }}
        </option>
      </select-field>
    </div>

    <permission-wrapper
      skip-alert
      page-name="announcements"
      resource-name="announcement"
      action-name="create">
      <div class="item-add">
        <a @click="redirectToDetails('new')">
          <a-icon
            type="plus-circle"
            :title="'announcement-list.lbl-add-announcement' | globalize"
            style="font-size: x-large"
          />
        </a>
      </div>
    </permission-wrapper>
    <a-list
      class="announcements-list"
      :loading="loading"
      bordered
      item-layout="vertical"
      :data-source="records"
    >
      <a-list-item
        slot="renderItem"
        slot-scope="item">
        <span slot="actions">
          <div class="announcements-actions">
            <span class="announcements-action-label">
              {{ "announcement-list.lbl-status" | globalize }}:
            </span>
            <span class="announcements-action-value">
              <announcement-status
                :icon-type="item.icon"
                :status="item.status"
              />
            </span>
          </div>
          <div
            class="announcements-actions"
            v-if="networks.length > 0"
          >
            <span class="announcements-action-label">
              {{ "announcement-list.lbl-network" | globalize }}:
            </span>
            <span class="announcements-action-value">
              <announcement-network
                :networks="networks"
                :chain-id="item.chainId"
              />
            </span>
          </div>
          <div class="announcements-actions">
            <span class="announcements-action-label">
              {{ "announcement-list.lbl-dates" | globalize }}::
            </span>
            <span class="announcements-action-value">
              <announcement-dates
                :start-date="item.startDate"
                :end-date="item.endDate"
              />
            </span>
          </div>

          <div class="announcements-actions">
            <span class="announcements-action-label">
              {{ "announcement-list.lbl-pages" | globalize }}:
            </span>
            <span class="announcements-action-value">
              <announcement-pages :pages="item.pages" />
            </span>
          </div>

          <div class="announcements-actions">
            <span class="announcements-action-label">
              {{ "announcement-list.lbl-countries" | globalize }}:
            </span>
            <span class="announcements-action-value">
              <announcement-countries :countries="item.countries" />
            </span>
          </div>
        </span>
        <a-list-item-meta>
          <a
            slot="title"
            class="announcement-label"
            @click="
              (ev) => {
                ev.stopPropagation();
                redirectToDetails(item.id);
              }
            "
          >
            <!-- eslint-disable -->
            <div
              slot="description"
              class="announcement-content ql-editor"
              :style="{ background: item.backgroundColor }"
              v-html="DOMPurify.sanitize(item.label)"
            />
            <!-- eslint-enable -->
          </a>
          <permission-wrapper
            skip-alert
            page-name="announcements"
            resource-name="announcement"
            action-name="delete">
            <announcement-remove
              slot="title"
              :on-click="() => onRemove(item.id)"
            />
          </permission-wrapper>
        </a-list-item-meta>
      </a-list-item>
    </a-list>

    <collection-loader
      class="app__more-btn-wrp"
      :page-limit="pageLimit"
      :first-page-loader="getData"
      @first-page-load="setList"
      @next-page-load="concatList"
      ref="collectionLoaderBtn"
    />
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import AIcon from 'ant-design-vue/lib/icon'
import AList from 'ant-design-vue/lib/list'
import SelectField from '@/vue/fields/SelectField'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import CollectionLoader from '@/vue/common/CollectionLoader'
import AnnouncementStatus from './components/AnnouncementStatus'
import AnnouncementPages from './components/AnnouncementPages'
import AnnouncementCountries from './components/AnnouncementCountries'
import AnnouncementDates from './components/AnnouncementDates'
import AnnouncementRemove from './components/AnnouncementRemove'
import AnnouncementNetwork from './components/AnnouncementNetwork'
import { vueRoutes } from '@/vue-router/routes'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { api } from '@/api'
import {
  AnnouncementRecord,
  ANNOUNCEMENT_STATUSES,
} from '@/js/records/entities/announcement.record'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'

const defaultFilters = {
  status: '',
  chainId: '',
}

export default {
  name: 'announcement-list',

  components: {
    CollectionLoader,
    SelectField,
    AnnouncementStatus,
    AnnouncementPages,
    AnnouncementCountries,
    AnnouncementDates,
    AnnouncementRemove,
    AnnouncementNetwork,
    AIcon,
    AList,
    'a-list-item': AList.Item,
    'a-list-item-meta': AList.Item.Meta,
    PermissionWrapper,
  },
  data () {
    return {
      showLoadingMore: true,
      loading: true,
      loadingMore: false,
      pageLimit: 15,
      records: [],
      networks: [],
      filters: defaultFilters,
      vueRoutes,
      ANNOUNCEMENT_STATUSES,
      DOMPurify,
    }
  },
  watch: {
    filters: {
      handler () {
        this.loading = true
        this.reloadList()
      },
      deep: true,
    },
    $route () {
      this.filters = queryToFilters(defaultFilters)
      return this.getData()
    },
  },
  async created () {
    this.filters = queryToFilters(defaultFilters)
    this.networks = await this.getNetworks()
  },

  methods: {
    async getNetworks () {
      this.loading = true
      this.isFailed = false
      let response = {}
      try {
        response = await api.getWithSignature('/supported-networks')
        return response.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    async getData () {
      this.loading = true
      this.isFailed = false
      let response = {}
      let query = {
        filter: clearObject({
          status: this.filters.status,
          chain_id: this.filters.chainId,
        }),
        page: {
          limit: this.pageLimit,
        },
      }

      try {
        response = await api.getWithSignature('/announcements/list', query)

        return response
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    setList (data) {
      if (data && data.map) {
        this.records = data.map((r) => {
          const result = new AnnouncementRecord(r)

          return result
        })
      } else {
        this.records = []
      }
    },

    concatList (data) {
      if (data.length) {
        this.records = this.records.concat(data)
      }
    },

    async reloadList () {
      addParamsToLocation(this.$route, this.filters)
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    async onRemove (id) {
      try {
        await api.deleteWithSignature(`/announcements/${id}`)

        await this.reloadList()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    redirectToDetails (id) {
      this.$router.push({
        name: vueRoutes.announcementDetails.name,
        params: { id },
      })
    },

    preview (record) {
      alert(`TODO implement me - ${JSON.stringify(record)}`)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@scss/variables';
@import '~quill/dist/quill.core.css';
@import '~ant-design-vue/lib/icon/style/index.css';
@import '~ant-design-vue/lib/pagination/style/index.css';
@import '~ant-design-vue/lib/list/style/index.css';

.announcements-actions {
  text-align: justify;
}

.announcements-action-label {
  padding-right: 0.08em;
}

.announcements-action-value {
  margin-right: 0.5em;
}

.item-add {
  margin-top: 1em;
  margin-bottom: 1em;
  width: 100%;
  text-align: right;
  cursor: pointer;
}

.announcement-content {
  box-sizing: border-box;
  margin: 0;
  font-variant: tabular-nums;
  list-style: none;
  font-feature-settings: 'tnum';
  cursor: pointer;
  position: relative;
  display: inline-table;
  width: 100%;
  height: 2em;
  padding: 0.25em 0.688em;
  border: 0.06em solid $_athensGray;
  border-radius: 0.25em;
  transition: all 0.3s;
}

.announcements-list {
  .ant-list-item {
    margin-top: 1.25em;
    margin-bottom: 1.25em;
    border-bottom: 0.06em solid $_silverChalice;
    background: white;

    .announcement-label {
      cursor: pointer;
      color: $_deepBlue;
    }
  }
}
</style>
